import { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Flame, Trophy, Clock, Target, TrendingUp, Calendar, Star, ChevronRight, Zap, Award, CalendarDays, Activity, BarChart3, Sparkles } from 'lucide-react';
import { usePilatesStore } from '../store/pilatesStore';
import type { Difficulty, MuscleGroup } from '../types';

export default function Progress() {
  const { sessions, progress, scheduledWorkouts, videos, updateProgress } = usePilatesStore();

  useEffect(() => {
    updateProgress();
  }, [sessions, updateProgress]);

  // Calculate enhanced stats
  const enhancedStats = useMemo(() => {
    const stats = {
      currentStreak: progress.currentStreak,
      longestStreak: progress.longestStreak,
      totalMinutes: progress.totalMinutes,
      totalWorkouts: progress.totalWorkouts,
      longestWorkout: 0,
      averageWorkout: 0,
      thisMonth: 0,
      lastMonth: 0,
      muscleGroups: {} as Record<MuscleGroup, number>,
      difficulties: {} as Record<Difficulty, number>,
      weeklyData: [0, 0, 0, 0, 0, 0, 0], // Last 7 days
      monthlyData: [] as { date: string; count: number }[],
    };

    if (sessions.length === 0) return stats;

    // Find longest workout
    stats.longestWorkout = Math.max(...sessions.map(s => s.duration));

    // Average workout duration
    stats.averageWorkout = Math.round(stats.totalMinutes / stats.totalWorkouts);

    // This month vs last month
    const now = new Date();
    const thisMonth = now.getMonth();
    const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const thisYear = now.getFullYear();
    const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

    sessions.forEach(session => {
      const date = new Date(session.date);
      const video = videos.find(v => v.id === session.videoId);
      
      // Count by muscle group
      if (video?.muscleGroup) {
        stats.muscleGroups[video.muscleGroup] = (stats.muscleGroups[video.muscleGroup] || 0) + 1;
      }
      
      // Count by difficulty
      if (video?.difficulty) {
        stats.difficulties[video.difficulty] = (stats.difficulties[video.difficulty] || 0) + 1;
      }

      // This month
      if (date.getMonth() === thisMonth && date.getFullYear() === thisYear) {
        stats.thisMonth++;
      }
      // Last month
      if (date.getMonth() === lastMonth && date.getFullYear() === lastMonthYear) {
        stats.lastMonth++;
      }

      // Weekly data (last 7 days)
      const sessionDate = new Date(session.date);
      const diffDays = Math.floor((now.getTime() - sessionDate.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays >= 0 && diffDays < 7) {
        stats.weeklyData[6 - diffDays]++;
      }

      // Monthly data for calendar
      const dateStr = session.date.split('T')[0];
      const existing = stats.monthlyData.find(m => m.date === dateStr);
      if (existing) {
        existing.count++;
      } else {
        stats.monthlyData.push({ date: dateStr, count: 1 });
      }
    });

    return stats;
  }, [sessions, videos, progress]);

  // Achievements
  const achievements = useMemo(() => {
    const all = [
      { id: 'first', name: 'First Steps', desc: 'Complete your first workout', icon: '🎯', earned: progress.totalWorkouts >= 1 },
      { id: 'three', name: 'Getting Started', desc: 'Complete 3 workouts', icon: '⭐', earned: progress.totalWorkouts >= 3 },
      { id: 'week', name: 'Week Warrior', desc: 'Achieve a 7-day streak', icon: '🔥', earned: progress.longestStreak >= 7 },
      { id: 'ten', name: 'Dedicated', desc: 'Complete 10 workouts', icon: '💪', earned: progress.totalWorkouts >= 10 },
      { id: 'hour', name: 'Hour Power', desc: 'Exercise for 60+ minutes total', icon: '⚡', earned: progress.totalMinutes >= 60 },
      { id: 'month', name: 'Monthly Master', desc: 'Complete 20 workouts', icon: '🏆', earned: progress.totalWorkouts >= 20 },
      { id: 'streak', name: 'Streak Legend', desc: 'Achieve a 14-day streak', icon: '🌟', earned: progress.longestStreak >= 14 },
      { id: 'champion', name: 'Champion', desc: 'Complete all 15 days', icon: '👑', earned: progress.totalWorkouts >= 15 },
    ];
    return { all, earned: all.filter(a => a.earned).length };
  }, [progress]);

  // Weekly goal progress
  const weeklyGoalPercentage = Math.min((progress.weeklyProgress / progress.weeklyGoal) * 100, 100);

  // Recent sessions
  const recentSessions = sessions
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 5);

  // Get video by ID helper
  const getVideoById = (id: string) => videos.find(v => v.id === id);

  const stats = [
    {
      icon: Flame,
      label: 'Current Streak',
      value: `${progress.currentStreak} days`,
      color: 'from-orange-400 to-red-500',
      bg: 'bg-orange-50',
      iconColor: 'text-orange-500'
    },
    {
      icon: Trophy,
      label: 'Longest Streak',
      value: `${progress.longestStreak} days`,
      color: 'from-amber-400 to-yellow-500',
      bg: 'bg-amber-50',
      iconColor: 'text-amber-500'
    },
    {
      icon: Clock,
      label: 'Total Minutes',
      value: `${progress.totalMinutes}`,
      color: 'from-blue-400 to-indigo-500',
      bg: 'bg-blue-50',
      iconColor: 'text-blue-500'
    },
    {
      icon: Target,
      label: 'Total Workouts',
      value: `${progress.totalWorkouts}`,
      color: 'from-rose-400 to-pink-500',
      bg: 'bg-rose-50',
      iconColor: 'text-rose-500'
    }
  ];

  const muscleGroupLabels: Record<MuscleGroup, string> = {
    'core': 'Core',
    'upper-body': 'Upper Body',
    'lower-body': 'Lower Body',
    'full-body': 'Full Body',
    'flexibility': 'Flexibility',
    'back': 'Back',
    'glutes': 'Glutes'
  };

  const difficultyLabels: Record<Difficulty, string> = {
    'beginner': 'Beginner',
    'intermediate': 'Intermediate',
    'advanced': 'Advanced'
  };

  const muscleGroupColors: Record<MuscleGroup, string> = {
    'core': 'bg-rose-500',
    'upper-body': 'bg-blue-500',
    'lower-body': 'bg-purple-500',
    'full-body': 'bg-gradient-to-r from-pink-500 to-purple-500',
    'flexibility': 'bg-teal-500',
    'back': 'bg-indigo-500',
    'glutes': 'bg-orange-500'
  };

  return (
    <div className="min-h-screen pb-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-purple-500 via-pink-500 to-rose-500 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-2">
              Your Progress
            </h1>
            <p className="text-xl text-white/90">
              Track your journey and celebrate your achievements
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
        >
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
              >
                <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center mb-3`}>
                  <Icon className={`w-5 h-5 ${stat.iconColor}`} />
                </div>
                <p className="text-gray-600 text-xs mb-1">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </motion.div>
            );
          })}
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          {/* Weekly Goal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Weekly Goal</h3>
                  <p className="text-gray-500 text-sm">{progress.weeklyProgress} of {progress.weeklyGoal} workouts</p>
                </div>
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {Math.round(weeklyGoalPercentage)}%
              </span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${weeklyGoalPercentage}%` }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
              />
            </div>
            {weeklyGoalPercentage >= 100 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-3 flex items-center gap-2 text-green-600 text-sm font-medium"
              >
                <Trophy className="w-4 h-4" />
                <span>Weekly goal achieved! 🎉</span>
              </motion.div>
            )}
          </motion.div>

          {/* Personal Records */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                <Award className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Personal Records</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Longest Workout</span>
                <span className="font-bold text-gray-900">{enhancedStats.longestWorkout} min</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Average Duration</span>
                <span className="font-bold text-gray-900">{enhancedStats.averageWorkout} min</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">This Month</span>
                <span className="font-bold text-gray-900">{enhancedStats.thisMonth} workouts</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">vs Last Month</span>
                <span className={`font-bold ${enhancedStats.thisMonth >= enhancedStats.lastMonth ? 'text-green-600' : 'text-red-500'}`}>
                  {enhancedStats.thisMonth >= enhancedStats.lastMonth ? '+' : ''}{enhancedStats.thisMonth - enhancedStats.lastMonth}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Weekly Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">This Week</h3>
            </div>
            <div className="flex items-end justify-between gap-2 h-20">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => {
                const maxWorkouts = Math.max(...enhancedStats.weeklyData, 1);
                const height = enhancedStats.weeklyData[index] > 0 
                  ? Math.max((enhancedStats.weeklyData[index] / maxWorkouts) * 100, 20) 
                  : 4;
                const isToday = index === new Date().getDay() - 1;
                return (
                  <div key={day} className="flex flex-col items-center flex-1">
                    <div 
                      className={`w-full rounded-t-md transition-all duration-500 ${isToday ? 'bg-gradient-to-t from-purple-500 to-pink-500' : 'bg-gray-200'}`}
                      style={{ height: `${height}%` }}
                    />
                    <span className={`text-xs mt-2 ${isToday ? 'text-purple-600 font-medium' : 'text-gray-500'}`}>{day}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-center text-sm text-gray-500 mt-2">
              {enhancedStats.weeklyData.reduce((a, b) => a + b, 0)} workouts this week
            </p>
          </motion.div>
        </div>

        {/* Achievements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mb-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Achievements</h3>
                <p className="text-gray-500 text-sm">{achievements.earned} of {achievements.all.length} earned</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {achievements.all.map((achievement, index) => (
              <motion.div
                key={achievement.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 + index * 0.05 }}
                className={`relative p-3 rounded-xl text-center transition-all ${
                  achievement.earned 
                    ? 'bg-gradient-to-br from-yellow-50 to-amber-50 border border-yellow-200' 
                    : 'bg-gray-50 border border-gray-100 opacity-50'
                }`}
              >
                <div className="text-2xl mb-1">{achievement.icon}</div>
                <p className={`text-xs font-medium ${achievement.earned ? 'text-gray-900' : 'text-gray-500'}`}>
                  {achievement.name}
                </p>
                {achievement.earned && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Muscle Group Distribution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-pink-500 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Muscle Groups</h3>
            </div>
            {Object.keys(enhancedStats.muscleGroups).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(enhancedStats.muscleGroups)
                  .sort(([,a], [,b]) => b - a)
                  .map(([group, count]) => {
                    const maxCount = Math.max(...Object.values(enhancedStats.muscleGroups));
                    const percentage = (count / maxCount) * 100;
                    return (
                      <div key={group}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">
                            {muscleGroupLabels[group as MuscleGroup] || group}
                          </span>
                          <span className="text-sm text-gray-500">{count}x</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${muscleGroupColors[group as MuscleGroup] || 'bg-gray-400'} rounded-full`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Complete workouts to see your muscle group stats</p>
            )}
          </motion.div>

          {/* Difficulty Distribution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Difficulty Breakdown</h3>
            </div>
            {Object.keys(enhancedStats.difficulties).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(enhancedStats.difficulties)
                  .sort(([,a], [,b]) => b - a)
                  .map(([diff, count]) => {
                    const maxCount = Math.max(...Object.values(enhancedStats.difficulties));
                    const percentage = (count / maxCount) * 100;
                    const colors: Record<string, string> = {
                      beginner: 'from-green-400 to-emerald-500',
                      intermediate: 'from-yellow-400 to-orange-500',
                      advanced: 'from-red-400 to-rose-500'
                    };
                    return (
                      <div key={diff}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">
                            {difficultyLabels[diff as Difficulty] || diff}
                          </span>
                          <span className="text-sm text-gray-500">{count}x</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full bg-gradient-to-r ${colors[diff] || 'bg-gray-400'} rounded-full`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Complete workouts to see your difficulty stats</p>
            )}
          </motion.div>
        </div>

        {/* Recent Sessions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900">Recent Sessions</h3>
            <TrendingUp className="w-5 h-5 text-gray-400" />
          </div>

          {recentSessions.length > 0 ? (
            <div className="space-y-4">
              {recentSessions.map((session, index) => {
                const video = getVideoById(session.videoId);
                const sessionDate = new Date(session.date);
                return (
                  <motion.div
                    key={session.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.55 + index * 0.1 }}
                    className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
                  >
                    <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white font-bold">
                      {video?.duration || 0}
                      <span className="text-xs ml-1">min</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{video?.title || 'Unknown Workout'}</p>
                      <p className="text-sm text-gray-500">
                        {sessionDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {' • '}
                        {video?.difficulty ? difficultyLabels[video.difficulty] : ''}
                      </p>
                    </div>
                    {session.rating && (
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-4 h-4 ${i < session.rating! ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                          />
                        ))}
                      </div>
                    )}
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <CalendarDays className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-2">No workouts yet</p>
              <p className="text-gray-400">Start your first workout to track your progress!</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
